import { Flex, Text } from '@chakra-ui/react';
import {
  faArchive,
  faArrowsRotate,
  faTriangleExclamation,
  IconDefinition
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, BadgeVariants } from '@morressier/alembic';
import {
  IEditorialPhase,
  IStateConfigEntryResponse,
  IWorkflowStateMeta
} from '@publishing-engine/domain/models';
import { useSubmissionData } from '../../contexts/SubmissionDataProvider';
import { calculateDaysToDeadline } from 'src/utils/deadlines';
import { formatDate, timeFormatDeadlineDate } from '../../utils/date';
import Divider from 'src/components/Divider';
import { DecisionMenuActions } from '@publishing-engine/domain/models/article-workflow';
import RecommendationBadge from 'src/components/RecommendationBadge';
import { EditorialWorkPhaseActions } from './WorkPhaseActions';
import RecommendationAction from './RecommendationAction';
import ProtectedActionButton from 'src/components/ProtectedAction/Button';
import { SubmissionPhase } from '../Organization/Workspace/Submissions';

type Props = {
  phase: IWorkflowStateMeta;
  showingDecisionsForPhase: null | string;
  decisionActionNames: DecisionMenuActions[];
  setShowingDecisionModal: (action: string) => void;
  latestRole: string | undefined;
  latestRecommendation: string | undefined;
  isCOCEnabled?: boolean;
  isCurrentOwner?: boolean;
  isCOCDecisionMaker?: boolean;
  workflowStateConfig: IStateConfigEntryResponse;
  setShowingDecisionsForPhase: React.Dispatch<
    React.SetStateAction<string | null>
  >;
};

const getBadgeContent = (
  hasDeadlinePassed: boolean,
  withdrawalRequested: boolean
): {
  variant: BadgeVariants;
  content: string;
  icon: IconDefinition;
} => {
  if (withdrawalRequested) {
    return {
      icon: faArchive,
      content: 'Withdrawal Requested',
      variant: 'attention'
    };
  }

  if (hasDeadlinePassed) {
    return {
      icon: faTriangleExclamation,
      content: 'Overdue',
      variant: 'caution'
    };
  }

  return {
    icon: faArrowsRotate,
    content: 'In Progress',
    variant: 'attention'
  };
};
// TODO: decide if we prefer to call this "Editorial"PhaseSection instead of "Work"PhaseSection.
export const DecisionMakerWorkPhase = ({
  phase,
  showingDecisionsForPhase,
  decisionActionNames,
  setShowingDecisionModal,
  latestRole,
  latestRecommendation,
  isCOCEnabled,
  isCurrentOwner,
  isCOCDecisionMaker,
  workflowStateConfig,
  setShowingDecisionsForPhase
}: Props) => {
  const {
    workflowResponse,
    submissionContext,
    submissionContextId,
    isDecisionMaker
  } = useSubmissionData();
  const currentPhaseDeadline = workflowResponse.workflowData.currentDeadline;
  const hasDeadlinePassed =
    calculateDaysToDeadline(new Date(currentPhaseDeadline ?? '')) < 1;
  const { decisionMenuConfig } = submissionContext?.config ?? {};
  // TODO: build helper class/utility, (submission, wfConfig) => per-state data (notReachedYet, active, completed-positive, completed-negative)
  const phaseName = phase.name as IEditorialPhase;

  const isShowingDecisionsOfOtherPhase =
    showingDecisionsForPhase && showingDecisionsForPhase !== phaseName;

  const withdrawalRequested =
    !!workflowResponse.workflowData.withdrawal?.requested;
  const badgeContent = getBadgeContent(hasDeadlinePassed, withdrawalRequested);
  const canMakeWorkflowDecision =
    isDecisionMaker && (!isCOCEnabled || isCOCDecisionMaker);
  const hasCOCEnabled = submissionContext?.config?.isCOCRecommendationRequired;

  if (isShowingDecisionsOfOtherPhase) {
    return <></>;
  }

  return (
    <>
      <Flex
        flexDirection="column"
        width="100%"
        backgroundColor="surfacePrimaryFill"
      >
        <Flex flexDirection="column">
          {/* ---- Heading --- */}
          <Flex
            justify="space-between"
            padding={3}
            paddingX={4}
            backgroundColor="surfaceSecondaryFill"
            mb={3}
          >
            <Flex gap={2} alignItems="center">
              <SubmissionPhase currentState={phase.name} />
              <Text textStyle="textContext">
                since{' '}
                {workflowResponse.workflowData.dateEnteredCurrentPhase
                  ? formatDate(
                      new Date(
                        workflowResponse.workflowData.dateEnteredCurrentPhase!
                      )
                    )
                  : null}
              </Text>
            </Flex>
            <Flex justifyContent="flex-end">
              {isCOCEnabled && (
                <EditorialWorkPhaseActions
                  phase={phase}
                  workflowStateConfig={workflowStateConfig}
                  showingDecisionsForPhase={showingDecisionsForPhase}
                  setShowingDecisionsForPhase={setShowingDecisionsForPhase}
                  setShowingDecisionModal={setShowingDecisionModal}
                  isCurrentOwner={isCurrentOwner}
                />
              )}
            </Flex>
          </Flex>
          <Flex flexDirection="column" paddingX={4}>
            {currentPhaseDeadline ? (
              <Flex
                gap={2}
                textStyle="textBody"
                textColor={hasDeadlinePassed ? 'errorFillInverse' : 'black'}
                alignItems="center"
              >
                <Badge size="context" variant={badgeContent.variant}>
                  <Flex gap={1} alignItems="center">
                    <FontAwesomeIcon icon={badgeContent.icon} />
                    <Text>{badgeContent.content}</Text>
                  </Flex>
                </Badge>
                <Text textStyle={'textContext'} isTruncated>
                  Due {timeFormatDeadlineDate(currentPhaseDeadline)}
                </Text>
              </Flex>
            ) : null}

            <Flex flexDirection="column" marginBottom={5}>
              {hasCOCEnabled && <RecommendationAction />}

              {decisionActionNames.length > 0 && (
                <>
                  {hasCOCEnabled && (
                    <Flex alignItems="center" gap={2} marginTop={1}>
                      <Text whiteSpace="nowrap" textStyle="textContext">
                        Latest Recommendation
                      </Text>
                      {Divider}
                      <Text whiteSpace="nowrap" textStyle="headingItem">
                        {latestRole ? latestRole + ' : ' : ''}
                      </Text>
                      <RecommendationBadge
                        recommendation={latestRecommendation ?? ''}
                      />
                    </Flex>
                  )}
                </>
              )}
              {/* --- Description --- */}
              {canMakeWorkflowDecision && (
                <Flex flexDirection="column" gap={4} marginTop={4}>
                  {decisionActionNames.map(action => {
                    return (
                      <ProtectedActionButton
                        relation="decision_maker"
                        entityType="journal_phase"
                        shouldWrapChildren={false}
                        entityId={`${submissionContextId}_${phaseName}`}
                        size="context"
                        as="button"
                        onClick={() => setShowingDecisionModal(action)}
                        variant={
                          latestRecommendation === action.toLowerCase()
                            ? 'primary'
                            : 'secondary'
                        }
                      >
                        {workflowStateConfig.actions.find(
                          workflowAction => workflowAction.name === action
                        )?.displayProperties.label ||
                          decisionMenuConfig?.[action]?.label ||
                          action}
                      </ProtectedActionButton>
                    );
                  })}
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};
